import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaChevronLeft, FaChevronRight, FaPlay, FaInstagram, FaYoutube, FaUsers, FaComment, FaHeart, FaExternalLinkAlt, FaPause } from 'react-icons/fa';

// Animations
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(50px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(30px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

// Styled Components
const CSection = styled.section`
    min-height: 100vh;
    background: linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 50%, #2d2d2d 100%);
    padding: 80px 20px;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
                radial-gradient(circle at 10% 20%, rgba(245, 102, 7, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 90% 80%, rgba(245, 102, 7, 0.05) 0%, transparent 50%);
    }

    @media (max-width: 768px) {
        padding: 60px 15px;
    }
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        max-width: 100%;
        padding: 0 10px;
    }
`;

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 60px;
    animation: ${fadeInUp} 1s ease-out;
`;

const SectionTitle = styled.h2`
    font-size: 2.8em;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #ffffff 0%, #f56607 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 2.2em;
    }

    @media (max-width: 480px) {
        font-size: 1.8em;
    }
`;

const SectionSubtitle = styled.p`
    font-size: 1.2em;
    color: #b0b0b0;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
        font-size: 1.1em;
        padding: 0 10px;
    }
`;

// Mobile-first grid layout
const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 50px;
    margin-bottom: 60px;

    @media (min-width: 1024px) {
        grid-template-columns: 1fr 1fr;
        gap: 60px;
        align-items: stretch;
    }
`;

const SectionCard = styled.div`
    animation: ${fadeInUp} 1s ease-out 0.2s both;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    padding: 30px;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: fit-content;

    &:hover {
        transform: translateY(-5px);
        border-color: rgba(245, 102, 7, 0.3);
    }

    @media (max-width: 768px) {
        padding: 25px 20px;
    }

    @media (max-width: 480px) {
        padding: 20px 15px;
    }
`;

const SectionLabel = styled.h3`
    font-size: 1.5em;
    color: #ffffff;
    margin-bottom: 25px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;

    svg {
        color: #f56607;
    }

    @media (max-width: 768px) {
        font-size: 1.3em;
        margin-bottom: 20px;
    }
`;

// App Slider Styles
const AppSliderContainer = styled.div`
    position: relative;
    border-radius: 15px;
    overflow: hidden;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
        max-width: 350px;
    }

    @media (max-width: 480px) {
        max-width: 300px;
    }
`;

const AppSliderTrack = styled.div<{ $translateX: number }>`
    display: flex;
    transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: translateX(${props => props.$translateX}%);
    height: 100%;
`;

const AppSlide = styled.div`
    min-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
`;

const AppImage = styled.img`
    width: 100%;
    max-width: 280px;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);

    @media (max-width: 768px) {
        max-width: 220px;
    }

    @media (max-width: 480px) {
        max-width: 200px;
    }
`;

const AppDescription = styled.p`
    color: #b0b0b0;
    text-align: center;
    margin-top: 15px;
    font-size: 0.95em;
    line-height: 1.5;
    padding: 0 10px;
`;

const NavButton = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: #ffffff;
    font-size: 1em;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;

    &:hover {
        background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
        border-color: transparent;
        transform: translateY(-50%) scale(1.1);
    }

    &.prev {
        left: 10px;
    }

    &.next {
        right: 10px;
    }

    @media (max-width: 480px) {
        width: 35px;
        height: 35px;
    }
`;

const DotsContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;
`;

const Dot = styled.button<{ $isActive: boolean }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$isActive ? '#f56607' : 'rgba(255, 255, 255, 0.3)'};
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #f56607;
        transform: scale(1.2);
    }
`;

// Video Section
const VideoContainer = styled.div<{ $aspectRatio: string }>`
    border-radius: 15px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    aspect-ratio: ${props => props.$aspectRatio};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;

    &:hover {
        border-color: rgba(245, 102, 7, 0.5);

        .play-button {
            transform: scale(1.1);
            background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
        }
    }

    @media (max-width: 768px) {
        max-width: 350px;
    }

    @media (max-width: 480px) {
        max-width: 300px;
    }
`;

const PlayButton = styled.div`
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: rgba(245, 102, 7, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.8em;
    transition: all 0.3s ease;
    box-shadow: 0 8px 25px rgba(245, 102, 7, 0.4);
    z-index: 2;

    @media (max-width: 768px) {
        width: 60px;
        height: 60px;
        font-size: 1.5em;
    }
`;

const PauseOverlay = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5em;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 2;

    ${VideoContainer}:hover & {
        opacity: 1;
    }

    @media (max-width: 768px) {
        width: 50px;
        height: 50px;
        font-size: 1.2em;
    }
`;

const VideoDescription = styled.p`
    color: #b0b0b0;
    text-align: center;
    margin-top: 15px;
    font-size: 0.95em;
    line-height: 1.5;
`;

// Social Media Sections
const SocialGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 40px;
    margin-top: 50px;

    @media (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
        gap: 40px;
    }

    @media (min-width: 1024px) {
        gap: 60px;
    }
`;

const SocialCard = styled.div`
    animation: ${slideIn} 1s ease-out 0.4s both;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    padding: 30px;
    transition: all 0.3s ease;
    text-align: center;

    &:hover {
        transform: translateY(-5px);
        border-color: rgba(245, 102, 7, 0.3);
        background: rgba(245, 102, 7, 0.05);
    }

    @media (max-width: 768px) {
        padding: 25px 20px;
        margin: 0 10px;
    }
`;

const SocialImageContainer = styled.div`
    position: relative;
    border-radius: 15px;
    overflow: hidden;
    margin-bottom: 20px;
    aspect-ratio: 1;
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SocialImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.3s ease;

    ${SocialCard}:hover & {
        transform: scale(1.05);
    }
`;

const SocialIconOverlay = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.3em;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

    @media (max-width: 768px) {
        width: 40px;
        height: 40px;
        font-size: 1.1em;
    }
`;

const SocialTitle = styled.h4`
    font-size: 1.4em;
    color: #ffffff;
    margin-bottom: 15px;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 1.2em;
    }
`;

const SocialDescription = styled.p`
    color: #b0b0b0;
    line-height: 1.6;
    margin-bottom: 25px;
    font-size: 0.95em;
`;

const SocialLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #f56607 0%, #ff8c00 100%);
    color: white;
    padding: 12px 24px;
    border-radius: 25px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(245, 102, 7, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(245, 102, 7, 0.4);
        gap: 12px;
    }

    @media (max-width: 768px) {
        padding: 10px 20px;
        font-size: 0.9em;
    }
`;

// Community Stats
const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 60px;
    animation: ${fadeInUp} 1s ease-out 0.6s both;

    @media (min-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 30px;
    }
`;

const StatCard = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 15px;
    padding: 25px 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    text-align: center;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(245, 102, 7, 0.1);
        border-color: rgba(245, 102, 7, 0.3);
        transform: translateY(-3px);
    }

    @media (max-width: 480px) {
        padding: 20px 15px;
    }
`;

const StatIcon = styled.div`
    font-size: 2.2em;
    color: #f56607;
    margin-bottom: 15px;
    display: flex;
    justify-content: center;

    @media (max-width: 768px) {
        font-size: 1.8em;
        margin-bottom: 12px;
    }
`;

const StatNumber = styled.div`
    font-size: 2em;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 8px;
    background: linear-gradient(135deg, #ffffff 0%, #f56607 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 1.6em;
    }
`;

const StatLabel = styled.div`
    color: #b0b0b0;
    font-size: 0.9em;
    font-weight: 500;

    @media (max-width: 768px) {
        font-size: 0.85em;
    }
`;

// Disclaimer Footer
const DisclaimerFooter = styled.footer`
    text-align: center;
    margin-top: 60px;
    padding: 20px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    color: #b0b0b0;
    font-size: 0.9em;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 0.85em;
        margin-top: 40px;
    }
`;

const CommunitySection: React.FC = () => {
    // App Slider State
    const [appCurrentIndex, setAppCurrentIndex] = useState(0);

    // Video State
    const [videoPlaying, setVideoPlaying] = useState(false);
    const [videoAspect, setVideoAspect] = useState('16/9');
    const videoRef = useRef<HTMLVideoElement>(null);

    // App Screenshots Data
    const appScreenshots = [
        {
            image: "/IMG_0662.PNG",
            description: ""
        },
        {
            image: "/IMG_0663.PNG",
            description: ""
        },
        {
            image: "/IMG_0664.PNG",
            description: ""
        },
        {
            image: "/IMG_0665.PNG",
            description: ""
        },
        {
            image: "/IMG_0666.PNG",
            description: ""
        },
        {
            image: "/IMG_0667.PNG",
            description: ""
        },
        {
            image: "/IMG_0668.PNG",
            description: ""
        },
        {
            image: "/IMG_0669.PNG",
            description: ""
        },
        {
            image: "/IMG_0670.PNG",
            description: ""
        },
        {
            image: "/IMG_0671.PNG",
            description: ""
        }
    ];

    // Slider Navigation Functions
    const nextAppSlide = () => {
        setAppCurrentIndex(prev => (prev + 1) % appScreenshots.length);
    };

    const prevAppSlide = () => {
        setAppCurrentIndex(prev => (prev - 1 + appScreenshots.length) % appScreenshots.length);
    };

    const appTranslateX = -appCurrentIndex * 100;

    // Video control functions
    const toggleVideoPlayback = () => {
        if (videoRef.current) {
            if (videoPlaying) {
                videoRef.current.pause();
                setVideoPlaying(false);
            } else {
                videoRef.current.play();
                setVideoPlaying(true);
            }
        }
    };

    const handleVideoEnd = () => {
        setVideoPlaying(false);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
    };

    const handleVideoMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const vid = e.currentTarget;
        if (vid.videoWidth && vid.videoHeight) {
            setVideoAspect(`${vid.videoWidth} / ${vid.videoHeight}`);
        }
    };

    return (
        <CSection>
            <Container>
                <SectionHeader>
                    <SectionTitle>Naša Komunita</SectionTitle>
                    <SectionSubtitle>
                        Pripojte sa k rastúcej komunite športových nadšencov. Zdieľajte skúsenosti, učte sa od expertov a zlepšujte sa spolu s nami !
                    </SectionSubtitle>
                </SectionHeader>

                {/* Main Content Grid */}
                <ContentGrid>
                    {/* App Showcase Section */}
                    <SectionCard>
                        <SectionLabel>
                            <FaUsers />
                            Naša Aplikácia
                        </SectionLabel>
                        <AppSliderContainer>
                            <AppSliderTrack $translateX={appTranslateX}>
                                {appScreenshots.map((screenshot, index) => (
                                    <AppSlide key={index}>
                                        <AppImage
                                            src={screenshot.image}
                                            alt={`App screenshot ${index + 1}`}
                                        />
                                        <AppDescription>{screenshot.description}</AppDescription>
                                    </AppSlide>
                                ))}
                            </AppSliderTrack>

                            <NavButton className="prev" onClick={prevAppSlide}>
                                <FaChevronLeft />
                            </NavButton>
                            <NavButton className="next" onClick={nextAppSlide}>
                                <FaChevronRight />
                            </NavButton>

                            <DotsContainer>
                                {appScreenshots.map((_, index) => (
                                    <Dot
                                        key={index}
                                        $isActive={index === appCurrentIndex}
                                        onClick={() => setAppCurrentIndex(index)}
                                    />
                                ))}
                            </DotsContainer>
                        </AppSliderContainer>
                    </SectionCard>

                    {/* Video Section */}
                    <SectionCard>
                        <SectionLabel>
                            <FaPlay />
                            Video Prehliadka
                        </SectionLabel>
                        <VideoContainer $aspectRatio={videoAspect} onClick={toggleVideoPlayback}>
                            <video
                                ref={videoRef}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '15px',
                                    display: videoPlaying ? 'block' : 'none'
                                }}
                                preload="metadata"
                                controls={videoPlaying}
                                onEnded={handleVideoEnd}
                                onLoadedMetadata={handleVideoMetadata}
                            >
                                <source src="/app-preview.mov" type="video/mp4" />
                                <source src="/app-preview.mov" type="video/quicktime" />
                                Váš prehliadač nepodporuje video element.
                            </video>

                            {!videoPlaying && (
                                <PlayButton className="play-button">
                                    <FaPlay />
                                </PlayButton>
                            )}

                            {videoPlaying && (
                                <PauseOverlay>
                                    <FaPause />
                                </PauseOverlay>
                            )}
                        </VideoContainer>
                        <VideoDescription>
                            Náhľad do našej aplikácie
                        </VideoDescription>
                    </SectionCard>
                </ContentGrid>

                {/* Social Media Grid */}
                <SocialGrid>
                    {/* Instagram Card */}
                    <SocialCard>
                        <SocialImageContainer>
                            <SocialImage
                                src="/instagram.png"
                                alt="Naša Instagram komunita - tipy a analýzy"
                            />
                            <SocialIconOverlay>
                                <FaInstagram />
                            </SocialIconOverlay>
                        </SocialImageContainer>
                        <SocialTitle>Sledujte nás na Instagrame</SocialTitle>
                        <SocialDescription>
                            Buďte súčasťou komunity – sledujte nás na Instagrame!
                        </SocialDescription>
                        <SocialLink
                            href="https://www.instagram.com/goly_esa_podcast/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FaInstagram />
                            Sledovať na Instagrame
                            <FaExternalLinkAlt />
                        </SocialLink>
                    </SocialCard>

                    {/* YouTube Card */}
                    <SocialCard>
                        <SocialImageContainer>
                            <SocialImage
                                src="/IMG_0674.PNG"
                                alt="Naša YouTube komunita - tutoriály a analýzy"
                            />
                            <SocialIconOverlay>
                                <FaYoutube />
                            </SocialIconOverlay>
                        </SocialImageContainer>
                        <SocialTitle>Odberajte na YouTube</SocialTitle>
                        <SocialDescription>
                            Podrobné analýzy zápasov, tutoriály a rozhovory
                            s úspešnými tipérmi. Naučte sa profesionálne stratégie tipovania od expertov!
                        </SocialDescription>
                        <SocialLink
                            href="https://www.youtube.com/@Futbalovýpodcast"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FaYoutube />
                            Odoberať na YouTube
                            <FaExternalLinkAlt />
                        </SocialLink>
                    </SocialCard>
                </SocialGrid>

                {/* Community Stats */}
                <StatsGrid>
                    <StatCard>
                        <StatIcon>
                            <FaUsers />
                        </StatIcon>
                        <StatNumber>6 000+</StatNumber>
                        <StatLabel>Fanúšikov! 🌟⚽️</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatIcon>
                            <FaComment />
                        </StatIcon>
                        <StatNumber>700+</StatNumber>
                        <StatLabel>Príspevkov</StatLabel>
                    </StatCard>
                </StatsGrid>

                {/* Disclaimer Footer */}
                <DisclaimerFooter>
                    18+ | Hrajte zodpovedne. | Všetok obsah má len informačný charakter a nepredstavuje hazardné poradenstvo. | Nie sme pridružení k žiadnemu kasínu alebo stávkovej stránke a na našej webovej stránke nemôžete priamo stávkovať.
                </DisclaimerFooter>
            </Container>
        </CSection>
    );
};

export default CommunitySection;